import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import ListSearch from '../../../components/ListSearch';
import { formatUtcToIst } from '../utils/datetime';
import { TicketDetailLink } from '../ticketNav';
import {
  ticketClientBranchLabel,
  parseTicketListResponse,
  TICKET_LIST_PAGE_SIZE,
} from '../api/ticketsApi';
import { ModuleShell, SkeletonBlock, EmptyState } from '../../../components/module-ui';

import { apiPath } from '../../../config/apiBase';
const API = apiPath('/api/tickets');

function authConfig(params = {}) {
  const token = localStorage.getItem('token');
  return {
    withCredentials: true,
    headers: { Authorization: `Bearer ${token}` },
    params,
  };
}

function MyRequestsPage() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchTickets = useCallback(async (pageNum, q) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/my-tickets`, authConfig({
        ...(q && q.trim() ? { search: q.trim() } : {}),
        limit: TICKET_LIST_PAGE_SIZE,
        skip: (pageNum - 1) * TICKET_LIST_PAGE_SIZE,
      }));
      const { items, total: count } = parseTicketListResponse(res);
      setTickets(items);
      setTotal(count);
    } catch {
      setTickets([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    const t = setTimeout(() => fetchTickets(page, searchQuery), 300);
    return () => clearTimeout(t);
  }, [page, searchQuery, fetchTickets]);

  const totalPages = Math.max(1, Math.ceil(total / TICKET_LIST_PAGE_SIZE));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  if (loading && tickets.length === 0) {
    return (
      <ModuleShell
        title="My Requests"
        subtitle="Raise a new request and track tickets you raised — you are notified on every update."
      >
        <SkeletonBlock />
      </ModuleShell>
    );
  }

  return (
    <ModuleShell
      title="My Requests"
      subtitle="Raise a new request and track tickets you raised — you are notified on every update."
      headerActions={(
        <Link
          to="/dashboard/tickets/new"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: '#002FA7', color: '#fff', textDecoration: 'none',
            padding: '10px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
            whiteSpace: 'nowrap',
          }}
        >
          + Raise a Request
        </Link>
      )}
    >
      <ListSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search your tickets…"
        resultCount={tickets.length}
        totalCount={total}
      />

      <div style={{ overflowX: 'auto', opacity: loading ? 0.7 : 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
              <th style={th}>Ticket</th>
              <th style={th}>Subject</th>
              <th style={th}>Domain</th>
              <th style={th}>Client / Branch</th>
              <th style={th}>Status</th>
              <th style={th}>Due</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={t.ticket_no} style={{ borderBottom: '1px solid #E5E7EB' }}>
                <td style={td}><TicketDetailLink ticketNo={t.ticket_no} style={{ color: '#002FA7', fontWeight: '600' }}>{t.ticket_no}</TicketDetailLink></td>
                <td style={td}>{t.subject}</td>
                <td style={td}>{t.domain}</td>
                <td style={td}>{ticketClientBranchLabel(t)}</td>
                <td style={td}><span style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '12px', background: '#EFF6FF', color: '#1E40AF' }}>{t.status}</span></td>
                <td style={td}>{t.due_date ? formatUtcToIst(t.due_date) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {total === 0 && (
          <EmptyState title="No tickets yet" description="Raise a request to get started." />
        )}
        {total > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <span style={{ fontSize: 12, color: '#6B7280' }}>
              Page {page} of {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="tk-btn tk-btn-secondary tk-btn-sm"
                disabled={!canPrev || loading}
                onClick={() => setPage((p) => p - 1)}
              >
                <CaretLeft size={14} /> Prev
              </button>
              <button
                type="button"
                className="tk-btn tk-btn-secondary tk-btn-sm"
                disabled={!canNext || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <CaretRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </ModuleShell>
  );
}

const th = { padding: '6px 8px', fontSize: '12px', fontWeight: '600' };
const td = { padding: '6px 8px', fontSize: '12px' };

export default MyRequestsPage;
